/* eslint-disable */
// Session 55 · Alpha Launch Legal Pages.
// Single component that renders EITHER /legal/terms or /legal/privacy by
// fetching the currently-published markdown from /api/legal/{kind}. Builds
// a scroll-spy Table of Contents from the H2 headings, exposes an in-page
// find/search field, and ships print-first CSS so the document is legally
// usable in a hard copy.

import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import api from "../lib/api";

const slugify = (t) =>
  (t || "")
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");

function renderMarkdown(md) {
  // Purpose-built lightweight renderer for our own legal markdown so we
  // don't pull in a full markdown-it dependency. Handles: H1/H2/H3, bullet
  // lists, --- separators, bold **x**, and paragraph breaks. All headings
  // get an id derived from the text so the TOC can anchor.
  const lines = (md || "").split("\n");
  const out = [];
  let listBuf = [];
  const flushList = () => {
    if (listBuf.length) {
      out.push({ kind: "ul", items: [...listBuf] });
      listBuf = [];
    }
  };
  for (const raw of lines) {
    const line = raw.trimEnd();
    if (/^\s*\*\s+/.test(line)) {
      listBuf.push(line.replace(/^\s*\*\s+/, ""));
      continue;
    }
    flushList();
    if (!line.trim()) { out.push({ kind: "br" }); continue; }
    if (line === "---") { out.push({ kind: "hr" }); continue; }
    let m;
    if ((m = line.match(/^#\s+(.+)$/))) { out.push({ kind: "h1", text: m[1] }); continue; }
    if ((m = line.match(/^##\s+(.+)$/))) { out.push({ kind: "h2", text: m[1] }); continue; }
    if ((m = line.match(/^###\s+(.+)$/))) { out.push({ kind: "h3", text: m[1] }); continue; }
    out.push({ kind: "p", text: line });
  }
  flushList();
  return out;
}

const emphasize = (text) => {
  // Bold **x** and inline emphasis. Split then reassemble as React nodes so
  // we can guard the emails/URLs against XSS by not using dangerouslySet.
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    const bold = p.match(/^\*\*([^*]+)\*\*$/);
    if (bold) return <strong key={i}>{bold[1]}</strong>;
    // Auto-link emails.
    const email = p.match(/^(.*?)([A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})(.*)$/);
    if (email) {
      return (
        <span key={i}>
          {email[1]}
          <a href={`mailto:${email[2]}`} className="legal-link">{email[2]}</a>
          {email[3]}
        </span>
      );
    }
    return <span key={i}>{p}</span>;
  });
};

export default function LegalDocument({ kind }) {
  const loc = useLocation();
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState("");
  const [query, setQuery] = useState("");
  const [activeAnchor, setActiveAnchor] = useState("");
  const mainRef = useRef(null);

  useEffect(() => {
    api.get(`/legal/${kind}`)
      .then((r) => setDoc(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Unable to load document"));
  }, [kind]);

  useEffect(() => {
    if (!doc || !loc.hash) return;
    const el = document.getElementById(loc.hash.slice(1));
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [doc, loc.hash]);

  const blocks = useMemo(() => (doc ? renderMarkdown(doc.content_markdown) : []), [doc]);
  const toc = useMemo(
    () => blocks.filter((b) => b.kind === "h2").map((b) => ({ id: slugify(b.text), label: b.text })),
    [blocks],
  );

  useEffect(() => {
    if (!toc.length) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActiveAnchor(e.target.id);
        });
      },
      { rootMargin: "-45% 0px -45% 0px", threshold: 0 },
    );
    toc.forEach((item) => {
      const el = document.getElementById(item.id);
      if (el) io.observe(el);
    });
    return () => io.disconnect();
  }, [toc]);

  const q = query.trim().toLowerCase();
  const matchCount = useMemo(() => {
    if (!q || !doc) return 0;
    return (doc.content_markdown.toLowerCase().match(new RegExp(q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g")) || []).length;
  }, [q, doc]);

  const highlight = (text) => {
    if (!q) return emphasize(text);
    const idx = text.toLowerCase().indexOf(q);
    if (idx < 0) return emphasize(text);
    return (
      <>
        {emphasize(text.slice(0, idx))}
        <mark className="legal-hi">{text.slice(idx, idx + q.length)}</mark>
        {emphasize(text.slice(idx + q.length))}
      </>
    );
  };

  return (
    <div className="legal-page" data-testid={`legal-${kind}-page`}>
      <header className="legal-topbar">
        <Link to="/" className="legal-brand" data-testid="legal-brand-home">
          <img src="/brand/metier-logo-gold.png" alt="Métier" />
        </Link>
        <nav className="legal-topnav">
          <Link to="/legal/terms" data-testid="legal-nav-terms">Terms</Link>
          <Link to="/legal/privacy" data-testid="legal-nav-privacy">Privacy</Link>
          <Link to="/support" data-testid="legal-nav-contact">Contact</Link>
          <button type="button" onClick={() => window.print()} className="legal-print" data-testid="legal-print">Print</button>
        </nav>
      </header>

      {err && <div className="legal-error" data-testid="legal-error">{err}</div>}

      {doc && (
        <div className="legal-shell">
          <aside className="legal-toc" aria-label="Table of contents" data-testid="legal-toc">
            <div className="legal-toc-header">
              <span className="legal-eyebrow">{doc.title}</span>
              <div className="legal-meta">
                <span data-testid="legal-version">Version {doc.version}</span>
                <span data-testid="legal-effective">Effective {doc.effective_date}</span>
              </div>
              <input
                type="search"
                className="legal-search"
                placeholder="Search this document…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="legal-search"
                aria-label="Search this document"
              />
              {q && (
                <div className="legal-search-hits" data-testid="legal-search-hits">
                  {matchCount} {matchCount === 1 ? "match" : "matches"}
                </div>
              )}
            </div>
            <ol className="legal-toc-list">
              {toc.map((item) => (
                <li key={item.id} className={activeAnchor === item.id ? "active" : ""}>
                  <a
                    href={`#${item.id}`}
                    data-testid={`legal-toc-${item.id}`}
                    onClick={() => setActiveAnchor(item.id)}
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ol>
          </aside>

          <main className="legal-body" ref={mainRef} data-testid={`legal-${kind}-body`}>
            {blocks.map((b, i) => {
              if (b.kind === "h1") return <h1 key={i} className="legal-h1">{b.text}</h1>;
              if (b.kind === "h2") {
                const id = slugify(b.text);
                return (
                  <h2 key={i} id={id} className="legal-h2">
                    <a href={`#${id}`} className="legal-anchor">§</a> {b.text}
                  </h2>
                );
              }
              if (b.kind === "h3") return <h3 key={i} className="legal-h3">{b.text}</h3>;
              if (b.kind === "ul") return (
                <ul key={i} className="legal-ul">
                  {b.items.map((it, j) => <li key={j}>{highlight(it)}</li>)}
                </ul>
              );
              if (b.kind === "hr") return <hr key={i} className="legal-hr" />;
              if (b.kind === "br") return null;
              return <p key={i} className="legal-p">{highlight(b.text)}</p>;
            })}

            <footer className="legal-footnote">
              <span>© 2026 Métier LLC · workmetier.com</span>
              <span>Version {doc.version} · Effective {doc.effective_date}</span>
            </footer>
          </main>
        </div>
      )}
    </div>
  );
}
